import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Briefcase, FileText, Eye, Sparkles, Search, Download, X } from 'lucide-react';
import { collection, getDocs, getDoc, updateDoc, doc, serverTimestamp, addDoc, query, where, deleteDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, getBytes } from 'firebase/storage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getCompanyIdForUser, findAccountIdByEmail, getPrimaryUserIdForAccount } from '../services/firestoreService';
import { isInternalUser } from '../services/accessControlService';
import BackButton from '../components/BackButton';
import EditButton from '../components/EditButton';
import SaveButton from '../components/SaveButton';
import SopRichEditor from '../components/sop/SopRichEditor';
import SopRichView from '../components/sop/SopRichView';
import { flattenStepContent } from '../utils/sopContent';
import { migrateJobDescriptionRecord } from '../utils/migrateJobDescriptionRecord';
import { generateDocumentPdf } from '../utils/documentPdf';
import { fetchGroupEmojis, defaultGroupEmojis } from '../services/groupEmojiService';
import { sopJsonToHtml } from '../components/sop/tiptap/sopJson';
import { fetchJdHtmlTemplate, renderJdHtmlTemplate, DEFAULT_JD_HTML_TEMPLATE } from '../services/jobDescriptionHtmlTemplateService';
import { downloadTailored } from '../utils/tailorDownloads';
import PortalBackground from '../components/PortalBackground';

/**
 * Portal Job Descriptions — mirrors the portal SOPs page. Each job description is
 * a single Tiptap document. Clients tailor their own copy (saved to
 * userJobCustomizations with contentJSON); the master jobDescriptions template is
 * never touched. The structured `details` stays as the permanent fallback.
 */
const JOB_SECTIONS = [
  { id: 'design', label: 'Design', emoji: '📐' },
  { id: 'construction', label: 'Construction', emoji: '👷' },
  { id: 'office', label: 'Office', emoji: '🏢' },
];

// overrideCompanyId/overrideUserId let an internal viewer (CRM Portal View)
// load a specific client's job descriptions instead of their own.
const JobDescriptions = ({ embedded = false, overrideCompanyId = null, overrideUserId = null }) => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const openParam = searchParams.get('open');
  // Guards the deep link so closing the modal doesn't immediately reopen it.
  const openedParamRef = useRef(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState(null);
  const [editedContentJSON, setEditedContentJSON] = useState(null);
  const [positions, setPositions] = useState({ design: [], construction: [], office: [] });
  const [userCustomizations, setUserCustomizations] = useState({});
  const [companyId, setCompanyId] = useState(null);
  const [isInternal, setIsInternal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [groupEmojis, setGroupEmojis] = useState(defaultGroupEmojis('jobDescription'));
  // Footer container node for the editor's formatting toolbar (portaled in).
  const [toolbarSlot, setToolbarSlot] = useState(null);

  useEffect(() => {
    fetchGroupEmojis('jobDescription').then(setGroupEmojis).catch(() => {});
  }, []);

  // Resolve company id so job descriptions are company-wide.
  useEffect(() => {
    if (overrideCompanyId) {
      setCompanyId(overrideCompanyId);
      return undefined;
    }
    if (!currentUser?.uid) {
      setCompanyId(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        let cid = await getCompanyIdForUser(currentUser.uid);
        if (!cid && currentUser.email) cid = await findAccountIdByEmail(currentUser.email, 'Clients');
        if (!cancelled) setCompanyId(cid || null);
      } catch {
        if (!cancelled) setCompanyId(null);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid, currentUser?.email, overrideCompanyId]);

  const getCompanyIdForSave = async () => {
    if (overrideCompanyId) return overrideCompanyId;
    if (companyId) return companyId;
    if (!currentUser?.uid) return null;
    try {
      let cid = await getCompanyIdForUser(currentUser.uid);
      if (!cid && currentUser.email) cid = await findAccountIdByEmail(currentUser.email, 'Clients');
      if (cid) setCompanyId(cid);
      return cid || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchJobDescriptions();
      fetchUserCustomizations();
    }
  }, [currentUser, companyId]);

  useEffect(() => {
    let cancelled = false;
    if (!currentUser?.uid) {
      setIsInternal(false);
      return undefined;
    }
    isInternalUser(currentUser.uid)
      .then((internal) => { if (!cancelled) setIsInternal(!!internal); })
      .catch(() => { if (!cancelled) setIsInternal(false); });
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  const fetchJobDescriptions = async () => {
    if (!currentUser) return;
    try {
      const snap = await getDocs(collection(db, 'jobDescriptions'));
      const jobs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const byOrder = (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
      setPositions({
        design: jobs.filter((j) => j.department === 'design').sort(byOrder),
        construction: jobs.filter((j) => j.department === 'construction').sort(byOrder),
        office: jobs.filter((j) => j.department === 'office').sort(byOrder),
      });
    } catch (error) {
      console.error('Error fetching job descriptions:', error);
    }
  };

  const fetchUserCustomizations = async () => {
    if (!currentUser) return;
    try {
      const collRef = collection(db, 'userJobCustomizations');
      let scopeDocs = [];
      if (companyId) {
        const accountSnap = await getDocs(query(collRef, where('accountId', '==', companyId)));
        const byId = new Map();
        accountSnap.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
        const primaryUid = await getPrimaryUserIdForAccount(companyId);
        if (primaryUid) {
          const primarySnap = await getDocs(query(collRef, where('userId', '==', primaryUid)));
          primarySnap.docs.forEach((d) => { if (!byId.has(d.id)) byId.set(d.id, { id: d.id, ...d.data() }); });
        }
        scopeDocs = Array.from(byId.values());
      } else {
        const snap = await getDocs(query(collRef, where('userId', '==', currentUser.uid)));
        scopeDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }
      const customizations = {};
      scopeDocs.forEach((d) => {
        if (!d.isCombined && d.templateId) customizations[d.templateId] = { id: d.id, ...d };
      });
      setUserCustomizations(customizations);
    } catch (error) {
      console.error('Error fetching job customizations:', error);
    }
  };

  const handleJobClick = useCallback((job, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!job || !job.id) return;
    try {
      const custom = userCustomizations[job.id];
      // Merge any customization over the template (title/details), like SOPs.
      const displayJob = custom
        ? { ...job, title: custom.title || job.title, details: custom.details || job.details }
        : { ...job };

      // Content precedence: client customization JSON → client's older tailored
      // `details` → template JSON → live conversion of the template `details`.
      let effectiveJSON;
      if (custom?.contentJSON) effectiveJSON = custom.contentJSON;
      else if (custom) effectiveJSON = migrateJobDescriptionRecord(displayJob);
      else effectiveJSON = job.contentJSON || migrateJobDescriptionRecord(job);
      effectiveJSON = flattenStepContent(effectiveJSON);

      displayJob.contentJSON = effectiveJSON;
      displayJob.templateId = job.id;
      displayJob.hasCustomizations = !!custom;

      setSelectedJob(displayJob);
      setEditedJob(displayJob);
      setEditedContentJSON(effectiveJSON);
      setIsEditing(false);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error opening job description:', error);
    }
  }, [userCustomizations]);

  // Sidebar search deep link: /portal/job-descriptions?open=<docId|title>.
  useEffect(() => {
    if (!openParam || openedParamRef.current === openParam) return;
    const all = [...positions.design, ...positions.construction, ...positions.office];
    if (all.length === 0) return;
    const wanted = openParam.toLowerCase();
    const match =
      all.find((job) => job.id === openParam) ||
      all.find((job) => (job.title || '').toLowerCase() === wanted);
    if (!match) return;
    openedParamRef.current = openParam;
    handleJobClick(match);
  }, [openParam, positions, handleJobClick]);

  // Tailor opens the same popup straight into edit mode.
  const handleJobTailor = (job, event) => {
    handleJobClick(job, event);
    setIsEditing(true);
  };

  const handleEdit = () => setIsEditing(true);

  const handleSave = async () => {
    try {
      const cid = await getCompanyIdForSave();
      const customizationData = {
        userId: overrideUserId || currentUser.uid,
        templateId: selectedJob.templateId,
        title: editedJob.title || '',
        department: editedJob.department || '',
        // Keep the structured details as a fallback; contentJSON is the tailored copy.
        details: selectedJob.details || {},
        contentJSON: editedContentJSON || selectedJob.contentJSON || migrateJobDescriptionRecord(editedJob),
        updatedAt: serverTimestamp(),
      };
      if (cid) customizationData.accountId = cid;

      if (selectedJob.hasCustomizations) {
        await updateDoc(doc(db, 'userJobCustomizations', userCustomizations[selectedJob.templateId].id), customizationData);
      } else {
        const docRef = await addDoc(collection(db, 'userJobCustomizations'), {
          ...customizationData,
          createdAt: serverTimestamp(),
        });
        setUserCustomizations((prev) => ({
          ...prev,
          [selectedJob.templateId]: { id: docRef.id, ...customizationData },
        }));
      }
      setSelectedJob({ ...editedJob, hasCustomizations: true });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving job customization:', error);
      alert('Error saving. Please try again.');
    }
  };

  const handleCancel = () => {
    setEditedJob({ ...selectedJob });
    setEditedContentJSON(selectedJob?.contentJSON || null);
    setIsEditing(false);
  };

  const handleResetToTemplate = async () => {
    try {
      if (!selectedJob.hasCustomizations) return;
      await deleteDoc(doc(db, 'userJobCustomizations', userCustomizations[selectedJob.templateId].id));
      setUserCustomizations((prev) => {
        const next = { ...prev };
        delete next[selectedJob.templateId];
        return next;
      });
      const templateJob = (positions[selectedJob.department] || []).find((j) => j.id === selectedJob.templateId);
      if (templateJob) {
        const resetJSON = flattenStepContent(templateJob.contentJSON || migrateJobDescriptionRecord(templateJob));
        const resetJob = { ...templateJob, templateId: templateJob.id, hasCustomizations: false, contentJSON: resetJSON };
        setSelectedJob(resetJob);
        setEditedJob(resetJob);
        setEditedContentJSON(resetJSON);
      }
    } catch (error) {
      console.error('Error resetting to template:', error);
    }
  };

  // Shared branding for JD downloads: logo (as a data URL so it renders in
  // html2canvas / Word), company name, and brand colors. Mirrors the SOPs helper.
  const getJdBranding = async () => {
    let companyLogo = null;
    let companyName = '';
    let primaryColor = '#dc2626';
    let secondaryColor = '#1f2937';
    const brandingUid = overrideUserId || currentUser?.uid;
    if (brandingUid) {
      try {
        const userDoc = await getDoc(doc(db, 'users', brandingUid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.companyLogo) companyLogo = userData.companyLogo;
          if (userData.company) companyName = userData.company;
          if (userData.companyColors) {
            primaryColor = userData.companyColors.primary || primaryColor;
            secondaryColor = userData.companyColors.secondary || secondaryColor;
          }
        }
        if (!companyLogo && !overrideUserId) {
          const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
          if (internalUserDoc.exists()) {
            const userData = internalUserDoc.data();
            if (userData.companyLogo) companyLogo = userData.companyLogo;
            if (!companyName && userData.company) companyName = userData.company;
            if (userData.companyColors) {
              primaryColor = userData.companyColors.primary || primaryColor;
              secondaryColor = userData.companyColors.secondary || secondaryColor;
            }
          }
        }
      } catch (e) {
        console.error('Error fetching user settings:', e);
      }
    }
    const loadLogoAsDataUrl = async (url) => {
      try {
        const isFirebaseStorageURL =
          url.includes('firebasestorage.googleapis.com') || url.includes('firebasestorage.app');
        if (isFirebaseStorageURL && currentUser) {
          const urlMatch = url.match(/\/o\/([^?]+)/);
          if (!urlMatch) return null;
          const storagePath = decodeURIComponent(urlMatch[1]);
          const storageRef = ref(storage, storagePath);
          const bytes = await getBytes(storageRef);
          let blobType = 'image/png';
          if (storagePath.endsWith('.svg')) blobType = 'image/svg+xml';
          else if (storagePath.endsWith('.jpg') || storagePath.endsWith('.jpeg')) blobType = 'image/jpeg';
          else if (storagePath.endsWith('.gif')) blobType = 'image/gif';
          else if (storagePath.endsWith('.webp')) blobType = 'image/webp';
          const blob = new Blob([bytes], { type: blobType });
          return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
          });
        }
        if (url.startsWith('http') || url.startsWith('/')) {
          const response = await fetch(url);
          if (!response.ok) return null;
          const blob = await response.blob();
          return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
          });
        }
        return null;
      } catch (e) {
        return null;
      }
    };
    let logoDataUrl = null;
    if (companyLogo) {
      const isFirebaseStorageURL =
        companyLogo.includes('firebasestorage.googleapis.com') || companyLogo.includes('firebasestorage.app');
      const timeout = isFirebaseStorageURL ? 5000 : 2000;
      logoDataUrl = await Promise.race([
        loadLogoAsDataUrl(companyLogo).catch(() => null),
        new Promise((r) => setTimeout(() => r(null), timeout)),
      ]);
    }
    if (!logoDataUrl) {
      logoDataUrl = await Promise.race([
        loadLogoAsDataUrl('/bba-logo.png').catch(() => null),
        new Promise((r) => setTimeout(() => r(null), 2000)),
      ]);
    }
    return { logoDataUrl, companyName, primaryColor, secondaryColor };
  };

  // Same document styles the shared documentPdf util uses, so templated and
  // built-in downloads look alike.
  const buildJdContentStyles = () => `<style>
    .doc-pdf h2 { font-size: 20px; font-weight: bold; margin: 16px 0 8px; color: #111; }
    .doc-pdf h3 { font-size: 18px; font-weight: bold; margin: 0 0 8px; color: #111; }
    .doc-pdf p { margin: 6px 0; font-size: 14px; color: #000; line-height: 1.6; }
    .doc-pdf ul { list-style: disc; padding-left: 22px; margin: 6px 0; }
    .doc-pdf ul ul { list-style: circle; }
    .doc-pdf ul ul ul { list-style: square; }
    .doc-pdf ol { list-style: decimal; padding-left: 22px; margin: 6px 0; }
    .doc-pdf li { font-size: 14px; color: #000; margin: 4px 0; }
  </style>`;

  // Render the job description through the master HTML template (CRM ▸ Job
  // Description Templates ▸ HTML Template tab). Returns null when no template
  // is saved so PDF can fall back to the built-in generateDocumentPdf layout.
  const buildJdDownloadHtml = async ({ requireTemplate = false } = {}) => {
    const [branding, master] = await Promise.all([getJdBranding(), fetchJdHtmlTemplate()]);
    const saved = (master.html || '').trim();
    if (!saved && requireTemplate) return null;
    const templateHtml = saved || DEFAULT_JD_HTML_TEMPLATE;
    const { logoDataUrl, companyName, primaryColor, secondaryColor } = branding;
    const contentJSON = selectedJob.contentJSON || migrateJobDescriptionRecord(selectedJob);
    const bodyHtml = `<div class="doc-pdf">${sopJsonToHtml(contentJSON)}</div>`;
    return (
      buildJdContentStyles() +
      renderJdHtmlTemplate(templateHtml, {
        logoUrl: logoDataUrl || '',
        title: selectedJob.title || '',
        department: selectedJob.department || '',
        body: bodyHtml,
        companyName,
        primaryColor,
        secondaryColor,
      })
    );
  };

  const jdFileName = () =>
    `${String(selectedJob.title || 'job_description').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_job_description`;

  const handleDownloadPDF = async () => {
    try {
      // Master template saved → templated render; otherwise keep the built-in
      // paginated layout (logo header + footer) from the shared util.
      const html = await buildJdDownloadHtml({ requireTemplate: true });
      if (html) {
        await downloadTailored('pdf', { html, fileName: jdFileName() });
        return;
      }
      const contentJSON = selectedJob.contentJSON || migrateJobDescriptionRecord(selectedJob);
      await generateDocumentPdf({
        title: selectedJob.title,
        contentJSON,
        currentUser,
        fileSuffix: 'job_description',
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Word download always goes through the template pipeline (starter layout
  // when none is saved) — mirrors the SOPs page.
  const handleDownloadDoc = async () => {
    try {
      const html = await buildJdDownloadHtml();
      await downloadTailored('docx', { html, fileName: jdFileName() });
    } catch (error) {
      console.error('Error generating document:', error);
      alert('Error generating document. Please try again.');
    }
  };

  // Search + group filter, mirroring the SOPs page.
  const q = searchQuery.trim().toLowerCase();
  const matchesSearch = (job, sectionLabel) => {
    if (!q) return true;
    const custom = userCustomizations[job.id];
    const title = (custom?.title || job.title || '').toLowerCase();
    return title.includes(q) || sectionLabel.toLowerCase().includes(q);
  };
  const visibleSections = JOB_SECTIONS
    .filter((s) => groupFilter === 'all' || s.id === groupFilter)
    .map((s) => ({ ...s, items: (positions[s.id] || []).filter((job) => matchesSearch(job, s.label)) }))
    .filter((s) => s.items.length > 0);

  const cardShell = `rounded-xl border p-4 transition-shadow hover:shadow-md cursor-pointer ${
    darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
  }`;
  const viewBtn = `flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
    darkMode ? 'border-gray-700 text-gray-200 hover:bg-gray-700/60' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
  }`;
  const tailorBtn =
    'flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors';
  // Once tailored, the Tailor button flips green so the state reads from the
  // action itself rather than a separate pill.
  const tailoredBtn =
    'flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors';
  // Template version next to the title (mirrors the Sales resources cards).
  const versionPill = `shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${
    darkMode ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-gray-100 text-gray-500 border border-gray-200'
  }`;
  const inputBase = `rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
    darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
  }`;

  const renderSectionHeader = (section) => (
    <div className="flex items-center justify-center gap-3 mb-5">
      <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
      <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
        <span className="text-xl leading-none">{groupEmojis[section.id] || section.emoji}</span>
      </span>
      <h2 className={`text-xl md:text-2xl font-bold tracking-tight leading-tight text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {section.label}
      </h2>
      <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
    </div>
  );

  const renderCard = (job) => {
    const custom = userCustomizations[job.id];
    const displayTitle = custom?.title || job.title || 'Untitled';
    const version = String(job.version || '').trim();
    return (
      <div key={job.id} className={cardShell} onClick={(event) => handleJobClick(job, event)}>
        <div className="flex items-center gap-3 mb-4">
          <span className="shrink-0 w-11 h-11 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center">
            {job.emoji ? <span className="text-2xl leading-none">{job.emoji}</span> : <FileText className="w-5 h-5" />}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h4 className={`text-base md:text-lg font-bold leading-snug truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {displayTitle}
              </h4>
              {version && <span className={versionPill}>v{version}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={(event) => handleJobClick(job, event)} className={viewBtn}>
            <Eye className="w-3.5 h-3.5" />
            View
          </button>
          <button type="button" onClick={(event) => handleJobTailor(job, event)} className={custom ? tailoredBtn : tailorBtn}>
            <Sparkles className="w-3.5 h-3.5" />
            {custom ? 'Tailored' : 'Tailor'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={embedded ? '' : 'relative min-h-screen p-6'}>
      {!embedded && <PortalBackground />}
      <div className={embedded ? 'relative w-full' : 'relative max-w-7xl mx-auto'}>
        <div className={embedded ? 'rounded-lg' : 'rounded-lg p-6'}>
          {!embedded && (
            <div className="flex items-center justify-between mb-8">
              <BackButton onClick={() => navigate('/portal/operations')} />
              <div className="flex items-center">
                <Briefcase className={`w-9 h-9 mr-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>Job Descriptions</h1>
              </div>
              <div className="w-[120px]" aria-hidden="true" />
            </div>
          )}

          {/* Search + group filter */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search job descriptions..."
                className={`w-full pl-9 pr-3 py-2 ${inputBase}`}
              />
            </div>
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className={`sm:w-56 px-3 py-2 ${inputBase}`}
            >
              <option value="all">All groups</option>
              {JOB_SECTIONS.map((s) => (
                <option key={s.id} value={s.id}>{(groupEmojis[s.id] || s.emoji)} {s.label}</option>
              ))}
            </select>
          </div>

          {visibleSections.length === 0 && (
            <p className={`text-sm text-center py-10 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No job descriptions match your search.
            </p>
          )}

          {visibleSections.map((section) => (
            <div key={section.id} className="mt-10 first:mt-0">
              {renderSectionHeader(section)}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.items.map((job) => renderCard(job))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedJob && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-[95vw] sm:max-w-[90vw] md:max-w-3xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col relative`}>
            {/* Close (top-left) */}
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              title="Close"
              aria-label="Close"
              className={`absolute top-3 left-3 z-10 p-1.5 rounded-full transition ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
            >
              <X className="w-5 h-5" />
            </button>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto thin-scrollbar px-4 sm:px-6 md:px-8 pt-12 pb-4">
              <div className="flex flex-col items-center mb-6 sm:mb-8">
                <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-center px-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {selectedJob.title}
                </h2>
              </div>

              <div className="space-y-4 sm:space-y-6">
                {isEditing ? (
                  <SopRichEditor
                    mode={isInternal ? 'full' : 'client'}
                    value={editedContentJSON}
                    onChange={setEditedContentJSON}
                    darkMode={darkMode}
                    toolbarSlot={toolbarSlot}
                  />
                ) : (
                  <SopRichView
                    value={selectedJob.contentJSON || migrateJobDescriptionRecord(selectedJob)}
                    darkMode={darkMode}
                  />
                )}
              </div>
            </div>

            {/* Footer — always visible */}
            <div className={`shrink-0 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} px-4 sm:px-6 md:px-8 py-4 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0`}>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                {isEditing && <div ref={setToolbarSlot} />}
                {selectedJob.hasCustomizations && (
                  <button
                    onClick={handleResetToTemplate}
                    className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-200 border border-transparent rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Reset to Template
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 sm:space-x-4 justify-end">
                {isEditing ? (
                  <>
                    <button
                      onClick={handleCancel}
                      className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                    >
                      Cancel
                    </button>
                    <SaveButton onClick={handleSave} variant="green" />
                  </>
                ) : (
                  <>
                    <EditButton label="Edit" onClick={handleEdit} />
                    <button
                      onClick={handleDownloadDoc}
                      className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Download DOC</span>
                      <span className="sm:hidden">DOC</span>
                    </button>
                    <button
                      onClick={handleDownloadPDF}
                      className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Download PDF</span>
                      <span className="sm:hidden">PDF</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDescriptions;
